import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AttendanceReportSummaryQuery,
  AttendanceReportTrendQuery,
  ClassCapacitySessionsQuery,
  ClassCapacitySummaryQuery,
  InstructorMonthlyPayoutLinesQuery,
  InstructorMonthlyPayoutQuery,
  InstructorSessionSummaryQuery,
  PackageSalesByClassTypeQuery,
  PackageSalesByTemplateQuery,
  PackageSalesSummaryQuery,
  RefundReportQuery,
  RevenueSummaryQuery,
  RevenueTrendQuery,
  RoomUsageSummaryQuery,
  ActiveStudentsByClassQuery,
  StudentReportLowRemainingQuery,
  StudentReportNewTrendQuery,
  StudentPackagesReportQuery,
  StudentReportSummaryQuery,
  StudentReportUtilizationQuery,
} from "./client";
import { reportsApi } from "./client";

export const reportKeys = {
  all: ["reports"] as const,
  attendanceSummary: (query: AttendanceReportSummaryQuery) =>
    ["reports", "attendance", "summary", query] as const,
  attendanceTrend: (query: AttendanceReportTrendQuery) =>
    ["reports", "attendance", "trend", query] as const,
  roomUsageSummary: (query: RoomUsageSummaryQuery) =>
    ["reports", "room-usage", "summary", query] as const,
};

export function useAttendanceReportSummaryQuery(
  query: AttendanceReportSummaryQuery,
) {
  return useQuery({
    queryKey: reportKeys.attendanceSummary(query),
    queryFn: () => reportsApi.attendance.getSummary(query),
  });
}

export function useAttendanceReportTrendQuery(
  query: AttendanceReportTrendQuery,
) {
  return useQuery({
    queryKey: reportKeys.attendanceTrend(query),
    queryFn: () => reportsApi.attendance.getTrend(query),
  });
}

export function useRoomUsageSummaryQuery(query: RoomUsageSummaryQuery) {
  return useQuery({
    queryKey: reportKeys.roomUsageSummary(query),
    queryFn: () => reportsApi.roomUsage.getSummary(query),
  });
}

export function useRoomUsageByRoomQuery(query: RoomUsageSummaryQuery) {
  return useQuery({
    queryKey: [...reportKeys.roomUsageSummary(query), "by-room"],
    queryFn: () => reportsApi.roomUsage.getByRoom(query),
  });
}

export function useRoomUsageTrendQuery(query: RoomUsageSummaryQuery) {
  return useQuery({
    queryKey: [...reportKeys.roomUsageSummary(query), "trend"],
    queryFn: () => reportsApi.roomUsage.getTrend(query),
  });
}

export function useRoomUsagePeakQuery(query: RoomUsageSummaryQuery) {
  return useQuery({
    queryKey: [...reportKeys.roomUsageSummary(query), "peak"],
    queryFn: () => reportsApi.roomUsage.getPeak(query),
  });
}

export const instructorSessionReportKeys = {
  summary: (query: InstructorSessionSummaryQuery) =>
    ["reports", "instructor-session", "summary", query] as const,
};

export function useInstructorSessionSummaryQuery(
  query: InstructorSessionSummaryQuery,
) {
  return useQuery({
    queryKey: instructorSessionReportKeys.summary(query),
    queryFn: () => reportsApi.instructorSession.getSummary(query),
  });
}

export function useInstructorSessionByInstructorQuery(
  query: InstructorSessionSummaryQuery,
) {
  return useQuery({
    queryKey: [...instructorSessionReportKeys.summary(query), "by-instructor"],
    queryFn: () => reportsApi.instructorSession.getByInstructor(query),
  });
}

export const instructorMonthlyPayoutKeys = {
  summary: (query: InstructorMonthlyPayoutQuery) =>
    ["reports", "instructor-monthly-payout", "summary", query] as const,
  lines: (query: InstructorMonthlyPayoutLinesQuery) =>
    ["reports", "instructor-monthly-payout", "lines", query] as const,
  payrollBatchMonth: (year: number, month: number) =>
    [
      "reports",
      "instructor-monthly-payout",
      "payroll-batch",
      year,
      month,
    ] as const,
  payrollBatchDetail: (batchId: string) =>
    [
      "reports",
      "instructor-monthly-payout",
      "payroll-batch-detail",
      batchId,
    ] as const,
};

export function useInstructorMonthlyPayoutSummaryQuery(
  query: InstructorMonthlyPayoutQuery | null,
) {
  return useQuery({
    queryKey: query
      ? instructorMonthlyPayoutKeys.summary(query)
      : (["reports", "instructor-monthly-payout", "summary", null] as const),
    queryFn: async () => {
      const q = query;
      if (q == null)
        throw new Error("instructor monthly payout: missing query");
      return reportsApi.instructorMonthlyPayout.getSummary(q);
    },
    enabled: query != null,
  });
}

export function useInstructorMonthlyPayoutLinesQuery(
  query: InstructorMonthlyPayoutLinesQuery | null,
) {
  return useQuery({
    queryKey: query
      ? instructorMonthlyPayoutKeys.lines(query)
      : (["reports", "instructor-monthly-payout", "lines", null] as const),
    queryFn: async () => {
      const q = query;
      if (q == null)
        throw new Error("instructor monthly payout: missing lines query");
      return reportsApi.instructorMonthlyPayout.getLines(q);
    },
    enabled: query != null,
  });
}

export function useInstructorPayrollBatchByMonthQuery(
  year: number | null,
  month: number | null,
) {
  return useQuery({
    queryKey:
      year != null && month != null
        ? instructorMonthlyPayoutKeys.payrollBatchMonth(year, month)
        : ([
            "reports",
            "instructor-monthly-payout",
            "payroll-batch",
            null,
          ] as const),
    queryFn: async () => {
      if (year == null || month == null) return null;
      return reportsApi.instructorMonthlyPayout.getPayrollBatchByMonth(
        year,
        month,
      );
    },
    enabled: year != null && month != null,
  });
}

export function useInstructorPayrollBatchDetailQuery(batchId: string | null) {
  return useQuery({
    queryKey: batchId
      ? instructorMonthlyPayoutKeys.payrollBatchDetail(batchId)
      : ([
          "reports",
          "instructor-monthly-payout",
          "payroll-batch-detail",
          null,
        ] as const),
    queryFn: async () => {
      if (batchId == null) return null;
      return reportsApi.instructorMonthlyPayout.getPayrollBatchById(batchId);
    },
    enabled: batchId != null,
  });
}

export function useUpsertInstructorPayoutLineOverrideMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      year: number;
      month: number;
      instructorId: string;
      sourceType: "class_session" | "private_booking" | "room_booking";
      sourceId: string;
      amountKip: number;
      note?: string | null;
    }) => reportsApi.instructorMonthlyPayout.upsertLineOverride(body),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: instructorMonthlyPayoutKeys.lines({
          year: variables.year,
          month: variables.month,
          instructorId: variables.instructorId,
        }),
      });
      void qc.invalidateQueries({
        queryKey: instructorMonthlyPayoutKeys.summary({
          year: variables.year,
          month: variables.month,
        }),
      });
      toast.success("ບັນທຶກຍອດແກ້ແລ້ວ");
    },
    onError: (e: Error) => {
      toast.error(e.message ?? "ບັນທຶກຍອດແກ້ບໍ່ສຳເລັດ");
    },
  });
}

export function useDeleteInstructorPayoutLineOverrideMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (query: {
      year: number;
      month: number;
      instructorId: string;
      sourceType: "class_session" | "private_booking" | "room_booking";
      sourceId: string;
    }) => reportsApi.instructorMonthlyPayout.deleteLineOverride(query),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: instructorMonthlyPayoutKeys.lines({
          year: variables.year,
          month: variables.month,
          instructorId: variables.instructorId,
        }),
      });
      void qc.invalidateQueries({
        queryKey: instructorMonthlyPayoutKeys.summary({
          year: variables.year,
          month: variables.month,
        }),
      });
      toast.success("ຄືນຍອດຕາມການຄິດໄລ່ແລ້ວ");
    },
    onError: (e: Error) => {
      toast.error(e.message ?? "ລຶບການແກ້ຍອດບໍ່ສຳເລັດ");
    },
  });
}

export function useCreateInstructorPayrollBatchMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      year: number;
      month: number;
      instructorIds: string[];
      notes?: string | null;
    }) => reportsApi.instructorMonthlyPayout.createPayrollBatch(body),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: instructorMonthlyPayoutKeys.payrollBatchMonth(
          variables.year,
          variables.month,
        ),
      });
      void qc.invalidateQueries({
        queryKey: ["reports", "instructor-monthly-payout", "payroll-batch-detail"],
      });
      toast.success("ບັນທຶກຮອບຄ່າຈ້າງແລ້ວ");
    },
    onError: (e: Error) => {
      toast.error(e.message ?? "ບັນທຶກບໍ່ສຳເລັດ");
    },
  });
}

export function useMarkInstructorPayrollBatchPaidMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { batchId: string; year: number; month: number }) =>
      reportsApi.instructorMonthlyPayout.markPayrollBatchPaid(vars.batchId),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({
        queryKey: instructorMonthlyPayoutKeys.payrollBatchMonth(
          variables.year,
          variables.month,
        ),
      });
      toast.success("ໝາຍວ່າຈ່າຍແລ້ວ");
    },
    onError: (e: Error) => {
      toast.error(e.message ?? "ອັບເດດບໍ່ສຳເລັດ");
    },
  });
}

export const revenueReportKeys = {
  summary: (query: RevenueSummaryQuery) =>
    ["reports", "revenue", "summary", query] as const,
};

export function useRevenueSummaryQuery(query: RevenueSummaryQuery) {
  return useQuery({
    queryKey: revenueReportKeys.summary(query),
    queryFn: () => reportsApi.revenue.getSummary(query),
  });
}

export function useRevenueTrendQuery(query: RevenueTrendQuery) {
  return useQuery({
    queryKey: [
      "reports",
      "revenue",
      "trend",
      query.dateFrom,
      query.dateTo,
      query.groupBy,
      query.issuedById,
    ],
    queryFn: () => reportsApi.revenue.getTrend(query),
  });
}

export const packageSalesReportKeys = {
  byTemplate: (query: PackageSalesByTemplateQuery) =>
    ["reports", "package-sales", "by-template", query] as const,
  byClassType: (query: PackageSalesByClassTypeQuery) =>
    ["reports", "package-sales", "by-class-type", query] as const,
  summary: (query: PackageSalesSummaryQuery) =>
    ["reports", "package-sales", "summary", query] as const,
};

export function usePackageSalesByTemplateQuery(
  query: PackageSalesByTemplateQuery,
) {
  return useQuery({
    queryKey: packageSalesReportKeys.byTemplate(query),
    queryFn: () => reportsApi.packageSales.getByTemplate(query),
  });
}

export function usePackageSalesSummaryQuery(query: PackageSalesSummaryQuery) {
  return useQuery({
    queryKey: packageSalesReportKeys.summary(query),
    queryFn: () => reportsApi.packageSales.getSummary(query),
  });
}

export function usePackageSalesByClassTypeQuery(
  query: PackageSalesByClassTypeQuery,
) {
  return useQuery({
    queryKey: packageSalesReportKeys.byClassType(query),
    queryFn: () => reportsApi.packageSales.getByClassType(query),
  });
}

export const refundReportKeys = {
  summary: (query: RefundReportQuery) =>
    ["reports", "refund", "summary", query] as const,
  reasons: (query: RefundReportQuery) =>
    ["reports", "refund", "reasons", query] as const,
};

export function useRefundReportSummaryQuery(query: RefundReportQuery) {
  return useQuery({
    queryKey: refundReportKeys.summary(query),
    queryFn: () => reportsApi.refund.getSummary(query),
  });
}

export function useRefundReportReasonsQuery(query: RefundReportQuery) {
  return useQuery({
    queryKey: refundReportKeys.reasons(query),
    queryFn: () => reportsApi.refund.getReasons(query),
  });
}

export const classCapacityReportKeys = {
  summary: (query: ClassCapacitySummaryQuery) =>
    ["reports", "class-capacity", "summary", query] as const,
  sessions: (query: ClassCapacitySessionsQuery) =>
    ["reports", "class-capacity", "sessions", query] as const,
  peakTimes: (query: ClassCapacitySummaryQuery) =>
    ["reports", "class-capacity", "peak-times", query] as const,
};

export function useClassCapacitySummaryQuery(query: ClassCapacitySummaryQuery) {
  return useQuery({
    queryKey: classCapacityReportKeys.summary(query),
    queryFn: () => reportsApi.classCapacity.getSummary(query),
  });
}

export function useClassCapacitySessionsQuery(
  query: ClassCapacitySessionsQuery,
) {
  return useQuery({
    queryKey: classCapacityReportKeys.sessions(query),
    queryFn: () => reportsApi.classCapacity.getSessions(query),
  });
}

export function useClassCapacityPeakTimesQuery(
  query: ClassCapacitySummaryQuery,
) {
  return useQuery({
    queryKey: classCapacityReportKeys.peakTimes(query),
    queryFn: () => reportsApi.classCapacity.getPeakTimes(query),
  });
}

export const studentReportKeys = {
  summary: (query: StudentReportSummaryQuery) =>
    ["reports", "students", "summary", query] as const,
  utilization: (query: StudentReportUtilizationQuery) =>
    ["reports", "students", "utilization", query] as const,
  newTrend: (query: StudentReportNewTrendQuery) =>
    ["reports", "students", "new-trend", query] as const,
  lowRemaining: (query: StudentReportLowRemainingQuery) =>
    ["reports", "students", "low-remaining", query] as const,
  packagesReport: (query: StudentPackagesReportQuery) =>
    ["reports", "students", "packages", query] as const,
  activeByClass: (query: ActiveStudentsByClassQuery) =>
    ["reports", "students", "active-by-class", query] as const,
};

export function useStudentReportSummaryQuery(query: StudentReportSummaryQuery) {
  return useQuery({
    queryKey: studentReportKeys.summary(query),
    queryFn: () => reportsApi.students.getSummary(query),
  });
}

export function useStudentReportUtilizationQuery(
  query: StudentReportUtilizationQuery,
) {
  return useQuery({
    queryKey: studentReportKeys.utilization(query),
    queryFn: () => reportsApi.students.getUtilization(query),
  });
}

export function useStudentReportNewTrendQuery(
  query: StudentReportNewTrendQuery,
) {
  return useQuery({
    queryKey: studentReportKeys.newTrend(query),
    queryFn: () => reportsApi.students.getNewTrend(query),
  });
}

export function useStudentReportLowRemainingQuery(
  query: StudentReportLowRemainingQuery,
) {
  return useQuery({
    queryKey: studentReportKeys.lowRemaining(query),
    queryFn: () => reportsApi.students.getLowRemaining(query),
  });
}

export function useStudentPackagesReportQuery(
  query: StudentPackagesReportQuery,
) {
  return useQuery({
    queryKey: studentReportKeys.packagesReport(query),
    queryFn: () => reportsApi.students.getPackagesReport(query),
  });
}

export function useActiveStudentsByClassQuery(
  query: ActiveStudentsByClassQuery,
) {
  return useQuery({
    queryKey: studentReportKeys.activeByClass(query),
    queryFn: () => reportsApi.students.getActiveByClass(query),
  });
}
